import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Modal,
  Button,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import {
  createMarginRecord,
  updateMarginRecord,
  clearMarginMessages,
} from "../../../store/marginManagement/marginSlice";

const MarginRecordModal = ({ open, onClose, record, requestingOrgCode }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: "",
    margin: "",
    effectiveStartDate: "",
    effectiveEndDate: "",
  });
  const [originalData, setOriginalData] = useState(null);
  const [nameValidationError, setNameValidationError] = useState("");
  const [marginType, setMarginType] = useState("discounts"); // "discounts" or "markup"

  const {
    isCreatingMarginRecord,
    isUpdatingMarginRecord,
    createMarginRecordSuccess,
    createMarginRecordError,
    updateMarginRecordSuccess,
    updateMarginRecordError,
  } = useSelector((state) => state.margin);
    console.log("🚀 ~ MarginRecordModal ~ updateMarginRecordError:", updateMarginRecordError)

  const isEditing = !!record;

  // Get tomorrow's date in YYYY-MM-DD format for date validation
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split("T")[0];

  // Check if the record being edited is upcoming (start date is in the future)
  const isUpcomingRecord = isEditing && record?.effectiveStartDate ? 
    new Date(record.effectiveStartDate) > new Date() : false;

  // Validate margin name according to backend pattern
  const validateMarginName = (name) => {
    if (!name || name.length > 128) {
      return false;
    }
    const pattern = /^[a-zA-Z0-9+&\- ]*$/;
    return pattern.test(name);
  };

  // Check if form is valid
  const isFormValid = () => {
    return (
      formData.name.trim() !== "" &&
      validateMarginName(formData.name.trim()) &&
      formData.margin !== "" &&
      parseFloat(formData.margin) > 0 &&
      formData.effectiveStartDate !== "" &&
      formData.effectiveEndDate !== "" &&
      new Date(formData.effectiveEndDate) >
        new Date(formData.effectiveStartDate) &&
      // For new records or upcoming records being edited, ensure start date is from tomorrow onwards
      ((!isEditing || isUpcomingRecord)
        ? new Date(formData.effectiveStartDate) >= new Date(tomorrowStr)
        : true)
    );
  };

  // Check if there are any changes when editing
  const hasChanges = () => {
    if (!isEditing || !originalData) return true; // For new records, always allow submission
    
    return (
      formData.name !== originalData.name ||
      formData.margin !== originalData.margin ||
      formData.effectiveStartDate !== originalData.effectiveStartDate ||
      formData.effectiveEndDate !== originalData.effectiveEndDate ||
      marginType !== originalData.marginType
    );
  };

  // Initialize form data when modal opens or record changes
  useEffect(() => {
    if (open) {
      if (record) {
        const marginValue = record.margin || "";
        const isNegative = parseFloat(marginValue) < 0;
        const marginTypeValue = isNegative ? "markup" : "discounts";
        const absoluteMargin = isNegative ? Math.abs(parseFloat(marginValue)) : marginValue;
        
        const initialData = {
          id: record.id,
          name: record.name || "",
          margin: absoluteMargin || "",
          effectiveStartDate: record.effectiveStartDate
            ? record.effectiveStartDate.split("T")[0]
            : "",
          effectiveEndDate: record.effectiveEndDate
            ? record.effectiveEndDate.split("T")[0]
            : "",
        };
        setFormData(initialData);
        setOriginalData({ ...initialData, marginType: marginTypeValue }); // Store original data for change detection
        setMarginType(marginTypeValue);
      } else {
        setFormData({
          name: "",
          margin: "",
          effectiveStartDate: "",
          effectiveEndDate: "",
        });
        setOriginalData(null);
        setMarginType("discounts");
      }
      // Clear any existing validation errors when modal opens
      setNameValidationError("");
      dispatch(clearMarginMessages());
    }
  }, [open, record, dispatch]);

  // Handle form field changes
  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // Date validation: if start date is after end date, clear end date
      if (
        field === "effectiveStartDate" &&
        newData.effectiveEndDate &&
        value > newData.effectiveEndDate
      ) {
        newData.effectiveEndDate = "";
      }

      return newData;
    });

    // Validate name field
    if (field === "name") {
      if (value.trim() && !validateMarginName(value.trim())) {
        setNameValidationError("Only letters, numbers, +, &, -, and spaces are allowed");
      } else {
        setNameValidationError("");
      }
    }
  };

  // Handle form submission
  const handleSubmit = () => {
    // Validation
    if (!formData.name.trim()) {
      return;
    }
    if (!validateMarginName(formData.name.trim())) {
      setNameValidationError("Only letters, numbers, +, &, -, and spaces are allowed");
      return;
    }
    if (!formData.margin || formData.margin <= 0) {
      return;
    }
    if (!formData.effectiveStartDate) {
      return;
    }
    if (!formData.effectiveEndDate) {
      return;
    }
    if (
      new Date(formData.effectiveEndDate) <=
      new Date(formData.effectiveStartDate)
    ) {
      return;
    }
    // For new records or upcoming records being edited, ensure start date is from tomorrow onwards
    if (
      (!isEditing || isUpcomingRecord) &&
      new Date(formData.effectiveStartDate) < new Date(tomorrowStr)
    ) {
      return;
    }

    const marginValue = parseFloat(formData.margin);
    const finalMargin = marginType === "markup" ? -marginValue : marginValue;

    const payload = {
      ...formData,
      requestingOrgCode,
      margin: finalMargin,
    };

    if (isEditing) {
      dispatch(updateMarginRecord(payload));
    } else {
      dispatch(createMarginRecord(payload));
    }
  };

  // Handle success/error messages
  useEffect(() => {
    if (createMarginRecordSuccess || updateMarginRecordSuccess) {
      // Clear form data after successful submission
      setFormData({
        name: "",
        margin: "",
        effectiveStartDate: "",
        effectiveEndDate: "",
      });
      setMarginType("discounts");
      setNameValidationError("");
      onClose();
      dispatch(clearMarginMessages());
    }
  }, [createMarginRecordSuccess, updateMarginRecordSuccess, onClose, dispatch]);

  const handleClose = () => {
    if (!isCreatingMarginRecord && !isUpdatingMarginRecord) {
      // Reset form state when closing modal
      setFormData({
        name: "",
        margin: "",
        effectiveStartDate: "",
        effectiveEndDate: "",
      });
      setOriginalData(null);
      setMarginType("discounts");
      setNameValidationError("");
      dispatch(clearMarginMessages());
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 600,
          bgcolor: "background.paper",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          borderRadius: "16px",
          overflow: "hidden",
          border: "1px solid #e2e8f0",
        }}
      >
        {/* Modal Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#f8fafc",
            padding: "20px 24px",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Box>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontWeight: 600,
                color: "#1e293b",
                fontSize: "1.25rem",
                mb: 0.5,
              }}
            >
              {isEditing ? "Edit Discount/Markup" : "Add Discount/Markup"}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={isCreatingMarginRecord || isUpdatingMarginRecord}
            sx={{
              color: "#64748b",
              "&:hover": {
                backgroundColor: "#f1f5f9",
                color: "#374151",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Modal Content */}
        <Box sx={{ padding: "24px" }}>
          {/* Error Messages */}
          {(createMarginRecordError || updateMarginRecordError) && (
            <Box
              sx={{
                mb: 3,
                p: 3,
                backgroundColor: "#fef2f2",
                borderRadius: "8px",
                border: "1px solid #f87171",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#991b1b",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    mb: 0.5,
                  }}
                >
                  {createMarginRecordError || updateMarginRecordError}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#9ca3af",
                    fontSize: "0.75rem",
                    fontWeight: 400,
                  }}
                >
                  Accepted characters: a-z, A-Z, 0-9, +, &, -, space
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => dispatch(clearMarginMessages())}
                sx={{
                  color: "#991b1b",
                  ml: 1,
                  "&:hover": {
                    backgroundColor: "#fee2e2",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}

          {/* Form Fields */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box>
              <TextField
                label="Name"
                value={formData.name}
                onChange={handleChange("name")}
                fullWidth
                required
                error={!!nameValidationError}
                disabled={isCreatingMarginRecord || isUpdatingMarginRecord}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: nameValidationError ? "#f87171" : "#d1d5db",
                    },
                    "&:hover fieldset": {
                      borderColor: nameValidationError ? "#f87171" : "#9ca3af",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: nameValidationError ? "#f87171" : "#3b82f6",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: nameValidationError ? "#f87171" : "#64748b",
                    fontWeight: 500,
                    "&.Mui-focused": {
                      color: nameValidationError ? "#f87171" : "#3b82f6",
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    padding: "12px 16px",
                  },
                }}
              />
              {nameValidationError && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#f87171",
                    fontSize: "0.75rem",
                    fontWeight: 400,
                    mt: 0.5,
                    ml: 1.5,
                  }}
                >
                  {nameValidationError}
                </Typography>
              )}
            </Box>

            <Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setMarginType("discounts")}
                  disabled={isCreatingMarginRecord || isUpdatingMarginRecord}
                  startIcon={
                    marginType === "discounts" ? (
                      <RadioButtonCheckedIcon />
                    ) : (
                      <RadioButtonUncheckedIcon />
                    )
                  }
                  sx={{
                    flex: 1,
                    borderColor: marginType === "discounts" ? "#3b82f6" : "#d1d5db",
                    backgroundColor: marginType === "discounts" ? "#eff6ff" : "transparent",
                    color: marginType === "discounts" ? "#3b82f6" : "#64748b",
                    fontWeight: marginType === "discounts" ? 600 : 500,
                    fontSize: "0.875rem",
                    padding: "10px 16px",
                    borderRadius: "8px",
                    textTransform: "none",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      borderColor: marginType === "discounts" ? "#2563eb" : "#9ca3af",
                      backgroundColor: marginType === "discounts" ? "#dbeafe" : "#f8fafc",
                      color: marginType === "discounts" ? "#2563eb" : "#374151",
                    },
                    "&:disabled": {
                      opacity: 0.5,
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: "1.25rem",
                    },
                  }}
                >
                  Discount
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setMarginType("markup")}
                  disabled={isCreatingMarginRecord || isUpdatingMarginRecord}
                  startIcon={
                    marginType === "markup" ? (
                      <RadioButtonCheckedIcon />
                    ) : (
                      <RadioButtonUncheckedIcon />
                    )
                  }
                  sx={{
                    flex: 1,
                    borderColor: marginType === "markup" ? "#3b82f6" : "#d1d5db",
                    backgroundColor: marginType === "markup" ? "#eff6ff" : "transparent",
                    color: marginType === "markup" ? "#3b82f6" : "#64748b",
                    fontWeight: marginType === "markup" ? 600 : 500,
                    fontSize: "0.875rem",
                    padding: "10px 16px",
                    borderRadius: "8px",
                    textTransform: "none",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      borderColor: marginType === "markup" ? "#2563eb" : "#9ca3af",
                      backgroundColor: marginType === "markup" ? "#dbeafe" : "#f8fafc",
                      color: marginType === "markup" ? "#2563eb" : "#374151",
                    },
                    "&:disabled": {
                      opacity: 0.5,
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: "1.25rem",
                    },
                  }}
                >
                  Markup
                </Button>
              </Box>
            </Box>

            <TextField
              label="%"
              type="number"
              value={formData.margin}
              onChange={handleChange("margin")}
              fullWidth
              required
              inputProps={{ min: 0, step: 0.01 }}
              disabled={
                isCreatingMarginRecord || isUpdatingMarginRecord || (isEditing && !isUpcomingRecord)
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: "#d1d5db",
                  },
                  "&:hover fieldset": {
                    borderColor: "#9ca3af",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#3b82f6",
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#64748b",
                  fontWeight: 500,
                  "&.Mui-focused": {
                    color: "#3b82f6",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  padding: "12px 16px",
                },
              }}
            />

            <TextField
              label="Effective Start Date"
              type="date"
              value={formData.effectiveStartDate}
              onChange={handleChange("effectiveStartDate")}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: (isEditing && !isUpcomingRecord) ? undefined : tomorrowStr,
              }}
              disabled={
                isCreatingMarginRecord || isUpdatingMarginRecord || (isEditing && !isUpcomingRecord)
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: "#d1d5db",
                  },
                  "&:hover fieldset": {
                    borderColor: "#9ca3af",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#3b82f6",
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#64748b",
                  fontWeight: 500,
                  "&.Mui-focused": {
                    color: "#3b82f6",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  padding: "12px 16px",
                },
              }}
            />

            <TextField
              label="Effective End Date"
              type="date"
              value={formData.effectiveEndDate}
              onChange={handleChange("effectiveEndDate")}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: (() => {
                  const today = new Date().toISOString().split('T')[0];
                  if (isEditing && !isUpcomingRecord) {
                    // When editing non-upcoming records, use the later of: start date or today
                    return formData.effectiveStartDate && formData.effectiveStartDate > today 
                      ? formData.effectiveStartDate 
                      : today;
                  } else {
                    // For new records or upcoming records being edited, use the later of: start date or tomorrow
                    return formData.effectiveStartDate && formData.effectiveStartDate > tomorrowStr 
                      ? formData.effectiveStartDate 
                      : tomorrowStr;
                  }
                })(),
              }}
              disabled={isCreatingMarginRecord || isUpdatingMarginRecord}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: "#d1d5db",
                  },
                  "&:hover fieldset": {
                    borderColor: "#9ca3af",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#3b82f6",
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#64748b",
                  fontWeight: 500,
                  "&.Mui-focused": {
                    color: "#3b82f6",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  padding: "12px 16px",
                },
              }}
            />
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{ display: "flex", gap: 2, mt: 4, justifyContent: "flex-end" }}
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={isCreatingMarginRecord || isUpdatingMarginRecord}
              sx={{
                borderColor: "#d1d5db",
                color: "#374151",
                fontWeight: 600,
                fontSize: "0.875rem",
                padding: "10px 20px",
                borderRadius: "8px",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f8fafc",
                },
                "&:disabled": {
                  borderColor: "#e5e7eb",
                  color: "#9ca3af",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                !isFormValid() ||
                !hasChanges() ||
                isCreatingMarginRecord ||
                isUpdatingMarginRecord
              }
              sx={{
                backgroundColor: (isFormValid() && hasChanges()) ? "#3b82f6" : "#9ca3af",
                color: "white",
                fontWeight: 600,
                fontSize: "0.875rem",
                padding: "10px 20px",
                borderRadius: "8px",
                textTransform: "none",
                boxShadow: (isFormValid() && hasChanges())
                  ? "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)"
                  : "none",
                "&:hover": {
                  backgroundColor: (isFormValid() && hasChanges()) ? "#2563eb" : "#9ca3af",
                  boxShadow: (isFormValid() && hasChanges())
                    ? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
                    : "none",
                },
                "&:active": {
                  backgroundColor: (isFormValid() && hasChanges()) ? "#1d4ed8" : "#9ca3af",
                },
                "&:disabled": {
                  backgroundColor: "#9ca3af",
                  color: "#6b7280",
                },
              }}
            >
              {(isCreatingMarginRecord || isUpdatingMarginRecord) && (
                <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
              )}
              {isEditing ? "Update" : "Create"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default MarginRecordModal;
